"use client"

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Search } from "lucide-react"

interface CriteriaTabProps {
  audioResult?: any
}

interface Criterion {
  id: string
  name: string
  status: "PASS" | "FAIL"
  score: number
  expected: string
  actual: string
  reason?: string
}

export function CriteriaTab({ audioResult }: CriteriaTabProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [filter, setFilter] = useState<"all" | "pass" | "fail">("all")

  // Transform scenario_details into criteria format
  const criteria: Criterion[] = (audioResult?.scenario_details || []).map((scenario: any, index: number) => ({
    id: scenario.scenario_id || `scenario-${index}`,
    name: scenario.scenario_id?.replace(/^APOLLO_/i, "").replace(/_/g, " ") || `Scenario ${index + 1}`,
    status: scenario.verdict === "PASS" ? "PASS" : "FAIL",
    score: scenario.verdict === "PASS" ? 100 : 0,
    expected: scenario.feedback?.summary || "Scenario should pass evaluation",
    actual: scenario.feedback?.analysis || scenario.reasoning?.join(" ") || "No details available",
    reason: scenario.verdict === "FAIL" ? (scenario.feedback?.root_cause || scenario.reasoning?.join(" ")) : undefined,
  }))

  const filteredCriteria = criteria.filter((criterion: Criterion) => {
    const matchesSearch = criterion.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesFilter =
      filter === "all" ||
      (filter === "pass" && criterion.status === "PASS") ||
      (filter === "fail" && criterion.status === "FAIL")

    return matchesSearch && matchesFilter
  })

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search criteria..."
            className="pl-10 border-border bg-white text-foreground"
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => setFilter("all")}
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            className={
              filter === "all" ? "bg-foreground text-background" : "border-border text-foreground hover:bg-muted"
            }
          >
            All
          </Button>
          <Button
            onClick={() => setFilter("pass")}
            variant={filter === "pass" ? "default" : "outline"}
            size="sm"
            className={
              filter === "pass" ? "bg-foreground text-background" : "border-border text-foreground hover:bg-muted"
            }
          >
            Pass
          </Button>
          <Button
            onClick={() => setFilter("fail")}
            variant={filter === "fail" ? "default" : "outline"}
            size="sm"
            className={
              filter === "fail" ? "bg-foreground text-background" : "border-border text-foreground hover:bg-muted"
            }
          >
            Fail
          </Button>
        </div>
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        {filteredCriteria.map((criterion) => (
          <AccordionItem
            key={criterion.id}
            value={criterion.id}
            className={`border rounded-lg overflow-hidden bg-white ${
              criterion.status === "PASS"
                ? "border-green-200"
                : "border-red-200"
            }`}
          >
            <AccordionTrigger className={`px-6 py-4 hover:no-underline ${
              criterion.status === "PASS"
                ? "hover:bg-green-50"
                : "hover:bg-red-50"
            }`}>
              <div className="flex items-center justify-between w-full pr-4">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-foreground">{criterion.name}</span>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                      criterion.status === "PASS"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {criterion.status === "PASS" ? "Pass" : "Fail"}
                  </span>
                </div>
                <span className="text-foreground font-semibold">{criterion.score}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4 border-t border-border bg-white">
              <div className="space-y-3 pt-4">
                {criterion.actual ? (
                  // For both passed and failed scenarios, show the structured analysis
                  <div>
                    <div className="text-foreground font-medium mb-2 text-sm">Analytics</div>
                    <div className={`text-muted-foreground text-sm whitespace-pre-wrap font-mono text-xs p-4 rounded border ${
                      criterion.status === "FAIL" ? "bg-gray-50" : "bg-green-50"
                    }`}>
                      {criterion.actual}
                    </div>
                  </div>
                ) : (
                  // Fallback: show summary if analysis is not available
                  criterion.expected && (
                    <div>
                      <div className="text-foreground font-medium mb-1 text-sm">Summary</div>
                      <div className="text-muted-foreground text-sm">{criterion.expected}</div>
                    </div>
                  )
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

